import './FundsManagement.css';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Wallet, RefreshCw } from 'lucide-react';
import { fundsAPI } from '../../../../services';
import notificationService from '../../../../services/notificationService';
import { handlePermissionError } from '../../../../services/utils/permissionErrorHandler';
import ExportButton from '../../../../components/ExportButton';
import StatsBar from './components/StatsBar';
import UserSearchPanel from './components/UserSearchPanel';
import FundsActionPanel from './components/FundsActionPanel';
import TransactionsPanel from './components/TransactionsPanel';
import StatementPanel from './components/StatementPanel';
import {
  formatCurrency,
  formatCompactCurrency,
  getCurrencySymbol,
  formatDate,
  getTransactionTypeColor,
  extractTransactionsData,
  extractBalanceData,
} from './components/utils';

const FundsManagement = () => {
  // ---- Core State ----
  const [transactions, setTransactions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ---- User Search State ----
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [users, setUsers] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);

  // ---- Selected User State ----
  const [selectedUser, setSelectedUser] = useState(null);
  const [userBalance, setUserBalance] = useState(null);

  // ---- Fund Action Form ----
  const [fundsForm, setFundsForm] = useState({
    userId: '',
    amount: '',
    description: '',
    action: 'add',
  });
  const [processing, setProcessing] = useState(false);

  // ---- Load transactions on mount ----
  useEffect(() => {
    fetchAllTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Sync userId when selectedUser changes ----
  useEffect(() => {
    if (selectedUser) {
      setFundsForm(prev => ({ ...prev, userId: selectedUser.id }));
    }
  }, [selectedUser]);

  // ---- Computed stats ----
  const stats = useMemo(() => {
    if (!transactions || transactions.length === 0) {
      return { totalCount: 0, totalVolume: 0, uniqueUsers: 0, successRate: 0, volumeByCurrency: {} };
    }
    const totalCount = transactions.length;
    const totalVolume = transactions.reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const uidSet = new Set();
    const volumeByCurrency = {};
    transactions.forEach(t => {
      const uid = t.user?.uid || t.userId;
      if (uid) uidSet.add(uid);
      const cur = (t.currency || 'USD').toUpperCase();
      volumeByCurrency[cur] = (volumeByCurrency[cur] || 0) + (Number(t.amount) || 0);
    });
    const completed = transactions.filter(
      t => t.status?.toLowerCase() === 'completed' || t.status?.toLowerCase() === 'success'
    ).length;
    const successRate = totalCount > 0 ? Math.round((completed / totalCount) * 100) : 0;
    return { totalCount, totalVolume, uniqueUsers: uidSet.size, successRate, volumeByCurrency };
  }, [transactions]);

  // ---- Export columns & data ----
  const transactionsExportData = useMemo(() => {
    if (!transactions) return [];
    return transactions.map(tx => ({
      transactionId: tx.transactionId || tx.id || '',
      userId: tx.user?.uid || tx.userId || '',
      type: tx.type || '',
      amount: Number(tx.amount) || 0,
      currency: tx.currency || 'USD',
      description: tx.description || '',
      status: tx.status || '',
      createdAt: tx.createdAt || tx.date || '',
    }));
  }, [transactions]);

  const transactionsExportColumns = [
    { key: 'transactionId', header: 'Transaction ID' },
    { key: 'userId', header: 'User' },
    { key: 'type', header: 'Type' },
    { key: 'amount', header: 'Amount', format: (v, row) => formatCurrency(v, row?.currency), align: 'right' },
    { key: 'currency', header: 'Currency' },
    { key: 'description', header: 'Description' },
    { key: 'status', header: 'Status' },
    { key: 'createdAt', header: 'Date', format: (v) => v ? formatDate(v) : '—' },
  ];

  // ==================== API CALLS ====================

  const fetchAllTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await fundsAPI.getAllTransactions({ limit: 50 });
      setTransactions(extractTransactionsData(result));
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
      setError(err.message || 'Failed to load transaction data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async (search) => {
    if (!search.trim()) {
      setUsers(null);
      return;
    }
    try {
      setUsersLoading(true);
      const result = await fundsAPI.getUsers({ search: search.trim(), limit: 10, page: 1 });
      if (result.success && result.data) {
        const userList = result.data.users || [];
        setUsers(userList.map(u => ({ ...u, id: u.id || u.accountId || u._id })));
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const fetchUserBalance = useCallback(async (userId) => {
    try {
      const result = await fundsAPI.getUserBalance(userId);
      setUserBalance(extractBalanceData(result));
    } catch (err) {
      console.error('Failed to fetch user balance:', err);
    }
  }, []);

  // ==================== DEBOUNCED SEARCH ====================
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers(userSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearchQuery, fetchUsers]);

  // ==================== HANDLERS ====================

  const handleSearchChange = (query) => {
    setUserSearchQuery(query);
  };

  const handleUserSelect = useCallback((user) => {
    const normalized = { ...user, id: user.id || user.accountId || user._id };
    setSelectedUser(normalized);
    setUsers(null);
    setUserSearchQuery('');
    fetchUserBalance(normalized.id);
  }, [fetchUserBalance]);

  const handleClearUser = useCallback(() => {
    setSelectedUser(null);
    setUserBalance(null);
    setFundsForm(prev => ({ ...prev, userId: '' }));
  }, []);

  const handleFundsAction = useCallback(async () => {
    if (!fundsForm.userId || !fundsForm.amount) return;

    try {
      setProcessing(true);
      setError('');

      const amount = parseFloat(fundsForm.amount);
      const adminInfo = { adminId: 'current_admin', adminName: 'Admin User' };
      let result;

      switch (fundsForm.action) {
        case 'add':
          result = await fundsAPI.addFunds(fundsForm.userId, amount, fundsForm.description, adminInfo);
          break;
        case 'remove':
          result = await fundsAPI.removeFunds(fundsForm.userId, amount, fundsForm.description, adminInfo);
          break;
        case 'adjust':
          result = await fundsAPI.adjustBalance(fundsForm.userId, amount, fundsForm.description, adminInfo);
          break;
        default:
          throw new Error('Invalid action');
      }

      if (result?.data?.success === false) {
        throw new Error(result.data.error || result.data.info || 'Transaction failed');
      }

      // Success — reset form amount+description but keep userId + action
      setFundsForm(prev => ({
        ...prev,
        amount: '',
        description: '',
      }));

      // Refresh
      if (selectedUser) fetchUserBalance(selectedUser.id);
      fetchAllTransactions();
      notificationService.success(`Funds ${fundsForm.action}ed successfully!`);
    } catch (err) {
      console.error('Funds action failed:', err);
      const isPermErr = handlePermissionError(err);
      if (!isPermErr) {
        setError(err.message || 'Failed to process funds action');
        notificationService.error(err.message || 'Failed to process funds action');
      }
    } finally {
      setProcessing(false);
    }
  }, [fundsForm, selectedUser, fetchUserBalance, fetchAllTransactions]);

  // ==================== RENDER ====================

  // Full-page loading
  if (loading) {
    return (
      <div className="funds-dashboard">
        <div className="state-msg" style={{ padding: '6rem 2rem' }}>
          <div className="spinner" />
          <h4>Loading funds data...</h4>
        </div>
      </div>
    );
  }

  // Full-page error
  if (error && !transactions) {
    return (
      <div className="funds-dashboard">
        <div className="state-msg" style={{ padding: '6rem 2rem' }}>
          <h4 style={{ color: '#dc2626' }}>Error Loading Data</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchAllTransactions} style={{ marginTop: '1rem' }}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="funds-dashboard">
      {/* ======== HEADER ======== */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>
            <Wallet />
            Funds Management
          </h1>
          <p>Manage user funds, view transactions, and monitor balances — all in one place.</p>
        </div>
        <div className="page-header-right">
          <ExportButton
            data={transactionsExportData}
            columns={transactionsExportColumns}
            filename="funds-management"
            title="Funds Management - Transactions"
            subtitle={`Exported on ${formatDate(new Date())}`}
          />
          <button className="btn btn-ghost" onClick={fetchAllTransactions}>
            <RefreshCw size={15} />
            Refresh
          </button>
        </div>
      </div>

      {/* ======== STATS BAR ======== */}
      <StatsBar stats={stats} formatCurrency={formatCurrency} formatCompactCurrency={formatCompactCurrency} getCurrencySymbol={getCurrencySymbol} />

      {/* ======== MAIN GRID: Left (Search + User Card) | Right (Actions) ======== */}
      <div className="main-grid">
        {/* ---- LEFT PANEL ---- */}
        <UserSearchPanel
          searchQuery={userSearchQuery}
          onSearchChange={handleSearchChange}
          users={users}
          usersLoading={usersLoading}
          selectedUser={selectedUser}
          onSelectUser={handleUserSelect}
          onClearUser={handleClearUser}
          userBalance={userBalance}
          formatCurrency={formatCurrency}
        />
        {/* ---- RIGHT PANEL ---- */}
        <FundsActionPanel
          fundsForm={fundsForm}
          setFundsForm={setFundsForm}
          processing={processing}
          error={error}
          onSubmit={handleFundsAction}
          selectedUser={selectedUser}
          userBalance={userBalance}
          formatCurrency={formatCurrency}
        />
      </div>

      {/* ======== TRANSACTIONS TABLE ======== */}
      <TransactionsPanel
        transactions={transactions}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
        getTransactionTypeColor={getTransactionTypeColor}
      />

      {selectedUser && (
        <StatementPanel
          userId={selectedUser.id}
          userEmail={selectedUser.username || selectedUser.id}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          onGenerateStatement={fundsAPI.getUserStatement.bind(fundsAPI)}
        />
      )}
    </div>
  );
};

export default FundsManagement;
